import React, { useState, useCallback, useRef } from 'react'
import { observer } from 'mobx-react-lite'
import { ReactSortable } from 'react-sortablejs'
import { SectionTab } from 'polotno/side-panel'
import { removeTags } from 'polotno/utils/text'
import { StoreType } from 'polotno/model/store'
import { LayersIcon } from '../hugeIcons/hugeIcons'
import './customLayersPanel.less'

// ─── Helpers ─────────────────────────────────────────────────────────────────

/** Pick a CSS modifier for the type badge */
const badgeClass = (type: string) => {
  const map: Record<string, string> = {
    video:  'video',
    text:   'text',
    svg:    'svg',
    image:  'image',
    figure: 'figure',
    audio:  'audio',
  }
  return `layer-row__badge--${map[type] ?? 'default'}`
}

/** Human-readable type label */
const typeLabel = (type: string) => type.charAt(0).toUpperCase() + type.slice(1)

// ─── Icons (inline SVG — zero extra deps) ────────────────────────────────────

const IconDrag = () => (
  <svg viewBox="0 0 16 16" fill="currentColor">
    <circle cx="5" cy="4" r="1.2"/><circle cx="5" cy="8" r="1.2"/><circle cx="5" cy="12" r="1.2"/>
    <circle cx="11" cy="4" r="1.2"/><circle cx="11" cy="8" r="1.2"/><circle cx="11" cy="12" r="1.2"/>
  </svg>
)
const IconEyeOpen = () => (
  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
    <path d="M1 8s2.5-5 7-5 7 5 7 5-2.5 5-7 5-7-5-7-5z"/>
    <circle cx="8" cy="8" r="2"/>
  </svg>
)
const IconEyeOff = () => (
  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
    <path d="M2 2l12 12M6.5 6.6A2 2 0 0 0 9.4 9.5M4.2 4.3C2.5 5.4 1 8 1 8s2.5 5 7 5c1.3 0 2.5-.4 3.5-1M7 3.1C7.3 3 7.7 3 8 3c4.5 0 7 5 7 5s-.8 1.5-2 2.8"/>
  </svg>
)
const IconLock = () => (
  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="7" width="10" height="7" rx="1.5"/>
    <path d="M5 7V5a3 3 0 0 1 6 0v2"/>
  </svg>
)
const IconUnlock = () => (
  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="7" width="10" height="7" rx="1.5"/>
    <path d="M5 7V5a3 3 0 0 1 5.2-1"/>
  </svg>
)
const IconTrash = () => (
  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M2 4h12M5 4V2.5C5 2 5.4 2 6 2h4c.6 0 1 0 1 .5V4M6 7v5M10 7v5M3 4l1 9.5C4 14 4.4 14 5 14h6c.6 0 1 0 1-.5L13 4"/>
  </svg>
)

// ─── Single layer row ─────────────────────────────────────────────────────────

interface LayerRowProps {
  element: any
  store: any
  isSelected: boolean
}

const LayerRow = observer(({ element, store, isSelected }: LayerRowProps) => {
  const [editing, setEditing] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const displayName =
    element.name ||
    (element.text ? removeTags(element.text) : '') ||
    `#${element.id}`

  const handleSelect = (e: React.MouseEvent) => {
    const multi = e.ctrlKey || e.metaKey || e.shiftKey
    const ids   = multi ? [...store.selectedElementsIds] : []
    if (!ids.includes(element.id)) ids.push(element.id)
    store.selectElements(ids)
  }

  const startEdit = (e: React.MouseEvent) => {
    e.stopPropagation()
    setEditing(true)
    setTimeout(() => inputRef.current?.select(), 0)
  }

  const commitEdit = useCallback((val: string) => {
    element.set({ name: val })
    setEditing(false)
  }, [element])

  const toggleVisibility = (e: React.MouseEvent) => {
    e.stopPropagation()
    element.set({ visible: !element.visible })
  }

  const toggleLock = (e: React.MouseEvent) => {
    e.stopPropagation()
    const locked = element.locked ?? false
    element.set({
      draggable:       locked,
      contentEditable: locked,
      styleEditable:   locked,
      resizable:       locked,
      removable:       locked,
    })
  }

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation()
    store.deleteElements([element.id])
  }

  return (
    <div
      className={`layer-row${isSelected ? ' layer-row--selected' : ''}`}
      onMouseDown={handleSelect}
    >
      {/* Drag grip */}
      <span className="layer-row__drag drag-handle">
        <IconDrag />
      </span>

      {/* Type badge */}
      <span className={`layer-row__badge ${badgeClass(element.type)}`}>
        {typeLabel(element.type)}
      </span>

      {/* Editable name */}
      <div className="layer-row__name" onDoubleClick={startEdit}>
        {editing ? (
          <input
            ref={inputRef}
            defaultValue={displayName}
            onBlur={(e)  => commitEdit(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter')  commitEdit((e.target as HTMLInputElement).value)
              if (e.key === 'Escape') setEditing(false)
            }}
            onClick={(e) => e.stopPropagation()}
          />
        ) : (
          <span title={displayName}>{displayName}</span>
        )}
      </div>

      {/* Actions */}
      <div className="layer-row__actions">
        <button
          className={`layer-row__btn${!element.visible ? ' layer-row__btn--hidden' : ''}`}
          title={element.visible ? 'Hide' : 'Show'}
          onClick={toggleVisibility}
        >
          {element.visible ? <IconEyeOpen /> : <IconEyeOff />}
        </button>

        <button
          className="layer-row__btn"
          title={element.locked ? 'Unlock' : 'Lock'}
          onClick={toggleLock}
        >
          {element.locked ? <IconLock /> : <IconUnlock />}
        </button>

        <button
          className="layer-row__btn layer-row__btn--delete"
          title="Delete"
          disabled={!element.removable}
          onClick={handleDelete}
        >
          <IconTrash />
        </button>
      </div>
    </div>
  )
})

// ─── Layers panel ─────────────────────────────────────────────────────────────

export const LayersPanel = observer(({ store }: { store: StoreType }) => {
  const activePage = (store as any).activePage
  const children: any[] = activePage?.children ?? []

  // Polotno stores elements bottom→top; reverse so top layer is at top of list.
  // Pre-filter non-selectable elements so ReactSortable never receives null children
  // (react-sortablejs calls React.Children.map internally and crashes on null).
  const allReversed = [...children].reverse()
  const visibleItems = allReversed
    .map((el: any) => ({ id: el.id, el }))
    .filter(({ el }) => el && el.selectable !== false)

  const listItems = visibleItems.map(({ id }) => ({ id }))

  const handleReorder = (newList: { id: string }[]) => {
    // Reorder maps only the visible subset; compute z-index relative to full children array
    newList.forEach(({ id }, listIndex) => {
      const zIndex  = newList.length - listIndex - 1
      const element = (store as any).getElementById(id)
      const current = activePage?.children.indexOf(element)
      if (element && current !== zIndex) activePage?.setElementZIndex(id, zIndex)
    })
  }

  return (
    <div className="layers-panel">
      {/* Header */}
      <div className="layers-panel__header">
        <p className="layers-panel__title">Layers</p>
        <span className="layers-panel__count">{visibleItems.length}</span>
      </div>

      {/* List */}
      <div className="layers-panel__list">
        {visibleItems.length === 0 ? (
          <div className="layers-panel__empty">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <rect x="2" y="7" width="20" height="14" rx="2"/>
              <path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/>
            </svg>
            No elements on this scene yet
          </div>
        ) : (
          <ReactSortable
            list={listItems}
            setList={handleReorder}
            handle=".drag-handle"
            animation={150}
            ghostClass="layer-row--dragging"
          >
            {visibleItems.map(({ id, el }) => (
              <LayerRow
                key={id}
                element={el}
                store={store}
                isSelected={(store as any).selectedElementsIds?.includes(id) ?? false}
              />
            ))}
          </ReactSortable>
        )}
      </div>
    </div>
  )
})

// ─── Section registration ─────────────────────────────────────────────────────

export const CustomLayersSection = {
  name: 'layers',
  Tab: (props: any) => (
    <SectionTab name="Layers" {...props}>
      <LayersIcon className="sidebar-panel-tab-icon" />
    </SectionTab>
  ),
  Panel: LayersPanel,
}
